// Source Documents — detail page.
//
// Route: /admin/knowledge/sources/:id
//
// Shows the SourceDocument's metadata + ingestion state + status-aware actions:
//
//   uploaded   → [Trigger ingestion] (super_admin + content_editor)
//   processing → "Processing…" with a spinner (no actions until done/failed)
//   ingested   → "{chunk_count} excerpts created" + link to filtered KB list
//   failed     → error message + [Retry ingestion]
//   removed    → [Restore] (super_admin only)
//
// All statuses (except removed): [Edit metadata] · [Remove]
//
// RBAC: same `sources:write` gate as the rest of the surface; Restore
// requires super_admin.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type {
  SourceDocument,
  SourceDocumentLanguage,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import {
  deleteSourceDocument,
  getSourceDocument,
  patchSourceDocument,
  restoreSourceDocument,
  triggerSourceDocumentIngestion,
} from '../api/endpoints.js';
import { formatRelativeTime } from '../users/time.js';
import { BookStructureCard } from './BookStructureCard.js';
import styles from './SourceDocuments.module.css';

const LANGUAGES: ReadonlyArray<SourceDocumentLanguage> = ['en', 'fr'];

type ConfirmKind = null | 'remove' | 'restore';

export function SourceDocumentDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t, tt } = useI18n();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [doc, setDoc] = useState<SourceDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  const [editPrefaceBy, setEditPrefaceBy] = useState('');
  const [editYear, setEditYear] = useState('');
  const [editIsbn, setEditIsbn] = useState('');
  const [editLanguage, setEditLanguage] = useState<SourceDocumentLanguage>('en');

  // Confirm panels
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [removeReason, setRemoveReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    const result = await getSourceDocument(id);
    if (result.ok) {
      setDoc(result.data.document);
      setEditTitle(result.data.document.title);
      setEditAuthor(result.data.document.author);
      setEditPrefaceBy(result.data.document.preface_by ?? '');
      setEditYear(
        result.data.document.publication_year != null
          ? String(result.data.document.publication_year)
          : '',
      );
      setEditIsbn(result.data.document.isbn ?? '');
      setEditLanguage(result.data.document.language);
      setError(null);
    } else {
      setError(t('sources.genericError'));
    }
    setIsLoading(false);
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function triggerIngestion() {
    if (!doc) return;
    setActionPending(true);
    setError(null);
    const result = await triggerSourceDocumentIngestion(doc.id);
    setActionPending(false);
    if (result.ok) {
      setDoc(result.data.document);
    } else {
      setError(t('sources.genericError'));
    }
  }

  async function saveMetadata() {
    if (!doc) return;
    setActionPending(true);
    setError(null);
    const result = await patchSourceDocument(doc.id, {
      title: editTitle.trim(),
      author: editAuthor.trim(),
      preface_by: editPrefaceBy.trim(),
      ...(editYear.trim() ? { publication_year: Number(editYear.trim()) } : {}),
      isbn: editIsbn.trim(),
      language: editLanguage,
    });
    setActionPending(false);
    if (result.ok) {
      setDoc(result.data.document);
      setEditing(false);
    } else {
      setError(t('sources.genericError'));
    }
  }

  async function remove() {
    if (!doc) return;
    setConfirm(null);
    setActionPending(true);
    setError(null);
    const reason = removeReason.trim();
    const result = await deleteSourceDocument(doc.id, reason || undefined);
    setActionPending(false);
    setRemoveReason('');
    if (result.ok) {
      setDoc(result.data.document);
    } else {
      setError(t('sources.genericError'));
    }
  }

  async function restore() {
    if (!doc) return;
    setConfirm(null);
    setActionPending(true);
    setError(null);
    const result = await restoreSourceDocument(doc.id);
    setActionPending(false);
    if (result.ok) {
      setDoc(result.data.document);
    } else if (result.code === 'FORBIDDEN') {
      setError(t('sources.restoreForbidden'));
    } else {
      setError(t('sources.genericError'));
    }
  }

  if (isLoading) {
    return (
      <AdminShell
        activeKey="sources"
        breadcrumb={[t('sources.breadcrumbKnowledge'), t('sources.breadcrumbSources')]}
        state={{ kind: 'loading' }}
      >
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spinner />
        </div>
      </AdminShell>
    );
  }

  if (!doc) {
    return (
      <AdminShell
        activeKey="sources"
        breadcrumb={[t('sources.breadcrumbKnowledge'), t('sources.breadcrumbSources')]}
        state={{ kind: 'normal' }}
      >
        <Alert severity="danger">{t('sources.notFound')}</Alert>
      </AdminShell>
    );
  }

  const status = doc.status;
  const isRemoved = status === 'removed';

  return (
    <AdminShell
      activeKey="sources"
      breadcrumb={[
        t('sources.breadcrumbKnowledge'),
        t('sources.breadcrumbSources'),
        doc.title,
      ]}
      state={{ kind: 'normal' }}
    >
      <PageHeader
        title={doc.title}
        sub={tt('sources.detailSubTemplate', { author: doc.author })}
        actions={
          <Button kind="ghost" onClick={() => navigate('/admin/knowledge/sources')}>
            {t('sources.backToList')}
          </Button>
        }
      />

      {error && (
        <div style={{ marginBottom: 12 }}>
          <Alert severity="danger" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Status banner */}
      <div className={styles.statusBanner}>
        <div className={statusPillClass(status, styles)}>{statusText(status, t)}</div>
        <StatusMessage doc={doc} t={t} tt={tt} />
      </div>

      {/* Book structure card — propose / review / approve flow */}
      <BookStructureCard doc={doc} onChange={load} />

      {/* Metadata block (read-only or edit) */}
      <section className={styles.metaBlock}>
        <header className={styles.metaBlockHeader}>
          <h3>{t('sources.metaSectionTitle')}</h3>
          {!isRemoved && !editing && (
            <Button kind="ghost" onClick={() => setEditing(true)} disabled={actionPending}>
              {t('sources.editMetadata')}
            </Button>
          )}
        </header>

        {editing ? (
          <div className={styles.metaGrid}>
            <EditField
              label={t('sources.fieldTitle')}
              value={editTitle}
              onChange={setEditTitle}
              disabled={actionPending}
            />
            <EditField
              label={t('sources.fieldAuthor')}
              value={editAuthor}
              onChange={setEditAuthor}
              disabled={actionPending}
            />
            <EditField
              label={t('sources.fieldPrefaceBy')}
              value={editPrefaceBy}
              onChange={setEditPrefaceBy}
              disabled={actionPending}
            />
            <EditField
              label={t('sources.fieldYear')}
              value={editYear}
              onChange={setEditYear}
              disabled={actionPending}
              type="number"
            />
            <EditField
              label={t('sources.fieldIsbn')}
              value={editIsbn}
              onChange={setEditIsbn}
              disabled={actionPending}
            />
            <div className={styles.metaField}>
              <label className={styles.fieldLabel}>{t('sources.fieldLanguage')}</label>
              <select
                className={styles.input}
                value={editLanguage}
                onChange={(e) =>
                  setEditLanguage(e.target.value as SourceDocumentLanguage)
                }
                disabled={actionPending}
              >
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <dl className={styles.metaList}>
            <ReadField label={t('sources.fieldAuthor')} value={doc.author} />
            <ReadField label={t('sources.fieldPrefaceBy')} value={doc.preface_by ?? '—'} />
            <ReadField
              label={t('sources.fieldYear')}
              value={doc.publication_year != null ? String(doc.publication_year) : '—'}
            />
            <ReadField label={t('sources.fieldIsbn')} value={doc.isbn ?? '—'} />
            <ReadField
              label={t('sources.fieldLanguage')}
              value={doc.language.toUpperCase()}
            />
            <ReadField
              label={t('sources.fieldFilename')}
              value={doc.original_filename}
            />
            <ReadField
              label={t('sources.fieldFileSize')}
              value={formatBytes(doc.file_size_bytes)}
            />
            {doc.chunk_count != null && (
              <ReadField label={t('sources.fieldChunkCount')} value={String(doc.chunk_count)} />
            )}
            <ReadField
              label={t('sources.fieldUploadedBy')}
              value={`${doc.uploaded_by} · ${formatRelativeTime(doc.uploadedAt)}`}
            />
            {doc.ingested_at && (
              <ReadField
                label={t('sources.fieldIngestedAt')}
                value={formatRelativeTime(doc.ingested_at)}
              />
            )}
            {doc.ingestion_error && (
              <ReadField
                label={t('sources.fieldIngestionError')}
                value={doc.ingestion_error}
              />
            )}
          </dl>
        )}

        {editing && (
          <div className={styles.editFooter}>
            <Button
              kind="ghost"
              onClick={() => setEditing(false)}
              disabled={actionPending}
            >
              {t('sources.editCancel')}
            </Button>
            <Button kind="primary" onClick={() => void saveMetadata()} disabled={actionPending}>
              {actionPending ? <Spinner /> : t('sources.editSave')}
            </Button>
          </div>
        )}
      </section>

      {/* Confirm panels */}
      {confirm === 'remove' && (
        <div className={styles.confirm} role="dialog" aria-modal="true">
          <h3 className={styles.confirmTitle}>{t('sources.confirmRemoveTitle')}</h3>
          <p className={styles.confirmBody}>{t('sources.confirmRemoveBody')}</p>
          <label className={styles.confirmLabel} htmlFor="sd-remove-reason">
            {t('sources.confirmRemoveReasonLabel')}
          </label>
          <textarea
            id="sd-remove-reason"
            className={styles.confirmTextarea}
            rows={3}
            maxLength={500}
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            placeholder={t('sources.confirmRemoveReasonPlaceholder')}
          />
          <div className={styles.confirmButtons}>
            <Button kind="ghost" onClick={() => {
              setConfirm(null);
              setRemoveReason('');
            }}>
              {t('sources.confirmCancel')}
            </Button>
            <Button kind="danger" onClick={() => void remove()}>
              {t('sources.confirmYes')}
            </Button>
          </div>
        </div>
      )}
      {confirm === 'restore' && (
        <div className={styles.confirm} role="dialog" aria-modal="true">
          <h3 className={styles.confirmTitle}>{t('sources.confirmRestoreTitle')}</h3>
          <p className={styles.confirmBody}>{t('sources.confirmRestoreBody')}</p>
          <div className={styles.confirmButtons}>
            <Button kind="ghost" onClick={() => setConfirm(null)}>
              {t('sources.confirmCancel')}
            </Button>
            <Button kind="primary" onClick={() => void restore()}>
              {t('sources.confirmYes')}
            </Button>
          </div>
        </div>
      )}

      {/* Action footer */}
      <footer className={styles.actionFooter}>
        {isRemoved ? (
          <Button
            kind="ghost"
            onClick={() => setConfirm('restore')}
            disabled={actionPending || !isSuperAdmin}
            title={isSuperAdmin ? undefined : t('sources.restoreSuperAdminOnly')}
          >
            {t('sources.restoreButton')}
          </Button>
        ) : (
          <>
            {status === 'uploaded' && (
              <Button kind="primary" onClick={() => void triggerIngestion()} disabled={actionPending}>
                {t('sources.triggerIngestionButton')}
              </Button>
            )}
            {status === 'failed' && (
              <Button kind="primary" onClick={() => void triggerIngestion()} disabled={actionPending}>
                {t('sources.retryIngestionButton')}
              </Button>
            )}
            <Button kind="danger" onClick={() => setConfirm('remove')} disabled={actionPending}>
              {t('sources.removeButton')}
            </Button>
          </>
        )}
      </footer>
    </AdminShell>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function StatusMessage({
  doc,
  t,
  tt,
}: {
  doc: SourceDocument;
  t: (k: string) => string;
  tt: (k: string, vars: Record<string, string | number>) => string;
}) {
  if (doc.status === 'uploaded') return <span>{t('sources.statusMessageUploaded')}</span>;
  if (doc.status === 'processing')
    return <span>{t('sources.statusMessageProcessing')}</span>;
  if (doc.status === 'ingested')
    return <span>{tt('sources.statusMessageIngested', { n: doc.chunk_count ?? 0 })}</span>;
  if (doc.status === 'failed')
    return <span>{t('sources.statusMessageFailed')}</span>;
  return <span>{t('sources.statusMessageRemoved')}</span>;
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metaRow}>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd className={styles.fieldValue}>{value}</dd>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  type?: 'text' | 'number';
}) {
  return (
    <div className={styles.metaField}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        type={type}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function statusPillClass(s: string, cls: Record<string, string>): string {
  const base = cls['statusPill'] ?? '';
  if (s === 'uploaded') return `${base} ${cls['statusUploaded'] ?? ''}`;
  if (s === 'processing') return `${base} ${cls['statusProcessing'] ?? ''}`;
  if (s === 'ingested') return `${base} ${cls['statusIngested'] ?? ''}`;
  if (s === 'failed') return `${base} ${cls['statusFailed'] ?? ''}`;
  return `${base} ${cls['statusRemoved'] ?? ''}`;
}

function statusText(s: string, t: (k: string) => string): string {
  if (s === 'uploaded') return t('sources.statusUploaded');
  if (s === 'processing') return t('sources.statusProcessing');
  if (s === 'ingested') return t('sources.statusIngested');
  if (s === 'failed') return t('sources.statusFailed');
  return t('sources.statusRemoved');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
